/**
 * Failed Job Retry Panel Component
 *
 * Management interface for failed jobs with bulk retry operations
 *
 * Features:
 * - Failed job list with error details
 * - Bulk retry operations
 * - Error pattern analysis
 * - Retry history tracking
 * - Selective job retry
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Table,
  Checkbox,
  Alert,
  Paper,
  Divider,
  Modal,
  Textarea,
  ActionIcon,
  Tooltip,
  LoadingOverlay,
  Accordion,
  Code
} from '@mantine/core';
import {
  IconReload,
  IconAlertCircle,
  IconTrash,
  IconEye,
  IconCheck,
  IconX,
  IconInfoCircle
} from '@tabler/icons-react';
import { useJobMonitoring, type JobStatus, type ErrorPattern } from '../hooks/useJobMonitoring';

export function FailedJobRetryPanel() {
  const {
    loading,
    retryJob,
    retryJobsBatch,
    getFailedJobs,
    errorPatterns,
    fetchErrorPatterns
  } = useJobMonitoring();

  const [failedJobs, setFailedJobs] = useState<JobStatus[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [detailsModalOpened, setDetailsModalOpened] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobStatus | null>(null);
  const [retrying, setRetrying] = useState(false);

  /**
   * Load failed jobs on mount
   */
  useEffect(() => {
    const loadFailedJobs = async () => {
      try {
        const jobs = await getFailedJobs(100);
        setFailedJobs(jobs);
      } catch (error) {
        console.error('Failed to load failed jobs:', error);
      }
    };

    loadFailedJobs();
    fetchErrorPatterns();
  }, [getFailedJobs, fetchErrorPatterns]);

  /**
   * Toggle job selection
   */
  const toggleJobSelection = (jobId: string) => {
    const newSelection = new Set(selectedJobs);
    if (newSelection.has(jobId)) {
      newSelection.delete(jobId);
    } else {
      newSelection.add(jobId);
    }
    setSelectedJobs(newSelection);
  };

  /**
   * Toggle all jobs selection
   */
  const toggleAllSelection = () => {
    if (selectedJobs.size === failedJobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(failedJobs.map(j => j.id)));
    }
  };

  /**
   * Handle single retry
   */
  const handleRetry = async (jobId: string) => {
    setRetrying(true);
    try {
      await retryJob(jobId);
      // Refresh failed jobs list
      const jobs = await getFailedJobs(100);
      setFailedJobs(jobs);
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setRetrying(false);
    }
  };

  /**
   * Handle batch retry
   */
  const handleBatchRetry = async () => {
    if (selectedJobs.size === 0) return;

    setRetrying(true);
    try {
      await retryJobsBatch(Array.from(selectedJobs));
      // Refresh failed jobs list
      const jobs = await getFailedJobs(100);
      setFailedJobs(jobs);
      setSelectedJobs(new Set());
    } catch (error) {
      console.error('Batch retry failed:', error);
    } finally {
      setRetrying(false);
    }
  };

  /**
   * View job details
   */
  const handleViewDetails = (job: JobStatus) => {
    setSelectedJob(job);
    setDetailsModalOpened(true);
  };

  /**
   * Format date
   */
  const formatDate = (isoString: string): string => {
    return new Date(isoString).toLocaleString();
  };

  /**
   * Get error severity color
   */
  const getErrorSeverityColor = (occurrenceCount: number): string => {
    if (occurrenceCount > 10) return 'red';
    if (occurrenceCount > 5) return 'orange';
    return 'yellow';
  };

  return (
    <Stack gap="lg">
      <LoadingOverlay visible={loading || retrying} />

      {/* Header */}
      <Group justify="space-between">
        <div>
          <Text fw={600} size="lg">Failed Jobs Management</Text>
          <Text size="sm" c="dimmed">
            {failedJobs.length} failed jobs | {selectedJobs.size} selected
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconReload size={16} />}
            onClick={handleBatchRetry}
            disabled={selectedJobs.size === 0}
            color="blue"
          >
            Retry Selected ({selectedJobs.size})
          </Button>
        </Group>
      </Group>

      {/* Error Patterns Alert */}
      {errorPatterns.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" title="Error Patterns Detected">
          <Text size="sm" mb="xs">
            {errorPatterns.length} recurring error patterns identified. Review patterns below for
            potential systemic issues.
          </Text>
        </Alert>
      )}

      {/* Error Patterns Accordion */}
      {errorPatterns.length > 0 && (
        <Card withBorder>
          <Text fw={600} mb="md">Error Patterns</Text>
          <Accordion variant="separated">
            {errorPatterns.map((pattern, index) => (
              <Accordion.Item key={index} value={`pattern-${index}`}>
                <Accordion.Control>
                  <Group justify="space-between">
                    <Group gap="sm">
                      <Badge color={getErrorSeverityColor(pattern.occurrenceCount)} size="sm">
                        {pattern.errorType}
                      </Badge>
                      <Text size="sm">{pattern.errorMessage.slice(0, 60)}...</Text>
                    </Group>
                    <Badge variant="light">{pattern.occurrenceCount} occurrences</Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    <div>
                      <Text size="xs" c="dimmed">Full Error Message</Text>
                      <Paper p="xs" withBorder bg="gray.0" mt="xs">
                        <Text size="xs" style={{ fontFamily: 'monospace' }}>
                          {pattern.errorMessage}
                        </Text>
                      </Paper>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Affected Job Types</Text>
                      <Group gap="xs" mt="xs">
                        {pattern.affectedJobTypes.map(type => (
                          <Badge key={type} size="xs" variant="outline">
                            {type.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </Group>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Time Range</Text>
                      <Text size="xs" mt="xs">
                        First: {formatDate(pattern.firstOccurrence)} →
                        Last: {formatDate(pattern.lastOccurrence)}
                      </Text>
                    </div>
                    {pattern.suggestion && (
                      <Alert icon={<IconInfoCircle size={14} />} color="blue" p="xs">
                        <Text size="xs" fw={600}>Suggestion:</Text>
                        <Text size="xs">{pattern.suggestion}</Text>
                      </Alert>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card>
      )}

      {/* Failed Jobs Table */}
      <Card withBorder>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Checkbox
                  checked={selectedJobs.size === failedJobs.length && failedJobs.length > 0}
                  indeterminate={selectedJobs.size > 0 && selectedJobs.size < failedJobs.length}
                  onChange={toggleAllSelection}
                />
              </Table.Th>
              <Table.Th>Job ID</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Failed At</Table.Th>
              <Table.Th>Retry Count</Table.Th>
              <Table.Th>Error</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {failedJobs.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Stack ta="center" gap="md" py="xl">
                    <IconCheck size={48} color="var(--mantine-color-green-6)" />
                    <Text c="dimmed" ta="center">
                      No failed jobs - All systems operating normally
                    </Text>
                  </Stack>
                </Table.Td>
              </Table.Tr>
            ) : (
              failedJobs.map((job) => (
                <Table.Tr key={job.id}>
                  <Table.Td>
                    <Checkbox
                      checked={selectedJobs.has(job.id)}
                      onChange={() => toggleJobSelection(job.id)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" style={{ fontFamily: 'monospace' }}>
                      {job.id.slice(0, 8)}...
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light">
                      {job.jobType.replace(/_/g, ' ')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{formatDate(job.createdAt)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="sm"
                      color={
                        (job.retryCount || 0) > 3 ? 'red' :
                        (job.retryCount || 0) > 1 ? 'orange' :
                        'yellow'
                      }
                    >
                      {job.retryCount || 0} retries
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="red" lineClamp={1}>
                      {job.failureReason || 'Unknown error'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => handleViewDetails(job)}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={() => handleRetry(job.id)}
                      >
                        <IconReload size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Retry Guidelines */}
      <Card withBorder>
        <Text fw={600} mb="md">Retry Guidelines</Text>
        <Stack gap="sm">
          <Group gap="xs">
            <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
            <Text size="sm">
              Failed jobs are automatically retried up to 3 times with exponential backoff
            </Text>
          </Group>
          <Group gap="xs">
            <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
            <Text size="sm">
              Manual retries reset the retry counter and queue the job immediately
            </Text>
          </Group>
          <Group gap="xs">
            <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
            <Text size="sm">
              Jobs that fail due to validation errors should be fixed before retrying
            </Text>
          </Group>
          <Group gap="xs">
            <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
            <Text size="sm">
              Investigate error patterns before bulk retrying similar failures
            </Text>
          </Group>
        </Stack>
      </Card>

      {/* Job Details Modal */}
      <Modal
        opened={detailsModalOpened}
        onClose={() => {
          setDetailsModalOpened(false);
          setSelectedJob(null);
        }}
        title="Failed Job Details"
        size="lg"
      >
        {selectedJob && (
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600}>Job ID:</Text>
              <Text style={{ fontFamily: 'monospace' }}>{selectedJob.id}</Text>
            </Group>
            <Group justify="space-between">
              <Text fw={600}>Type:</Text>
              <Badge>{selectedJob.jobType}</Badge>
            </Group>
            <Group justify="space-between">
              <Text fw={600}>Status:</Text>
              <Badge color="red">{selectedJob.status}</Badge>
            </Group>
            <Group justify="space-between">
              <Text fw={600}>Failed At:</Text>
              <Text>{formatDate(selectedJob.createdAt)}</Text>
            </Group>
            <Group justify="space-between">
              <Text fw={600}>Retry Count:</Text>
              <Badge color={(selectedJob.retryCount || 0) > 2 ? 'red' : 'yellow'}>
                {selectedJob.retryCount || 0} retries
              </Badge>
            </Group>
            {selectedJob.lastRetryAt && (
              <Group justify="space-between">
                <Text fw={600}>Last Retry:</Text>
                <Text>{formatDate(selectedJob.lastRetryAt)}</Text>
              </Group>
            )}
            <Divider />
            {selectedJob.failureReason && (
              <div>
                <Text fw={600} mb="xs">Error Details</Text>
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                  <Text size="sm" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {selectedJob.failureReason}
                  </Text>
                </Alert>
              </div>
            )}
            {selectedJob.metadata && Object.keys(selectedJob.metadata).length > 0 && (
              <div>
                <Text fw={600} mb="xs">Job Metadata</Text>
                <Paper p="sm" withBorder bg="gray.0">
                  <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(selectedJob.metadata, null, 2)}
                  </pre>
                </Paper>
              </div>
            )}
            <Divider />
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => setDetailsModalOpened(false)}
              >
                Close
              </Button>
              <Button
                color="blue"
                leftSection={<IconReload size={16} />}
                onClick={() => {
                  handleRetry(selectedJob.id);
                  setDetailsModalOpened(false);
                }}
              >
                Retry Job
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
